import { defineResource } from '@tnn/core'
import { z } from 'zod'

export const user = defineResource({
  app: 'admin',
  name: 'user',
  path: 'users',
  schema: z.object({
    name: z.string().trim().min(1).max(120),
    username: z
      .string()
      .trim()
      .toLowerCase()
      .regex(/^[a-z0-9._-]{3,30}$/),
    email: z.email().nullish(),
    phone: z.string().trim().max(20).nullish(),
    roleIds: z.array(z.string()).default([]),
    /** Home branch: new waybills start there and its receipts are numbered there */
    branchId: z.string().nullish(),
    active: z.boolean().default(true),
    /** Required when creating a user; never returned by the API */
    password: z.string().min(8).max(100).optional(),
  }),
  fields: {
    name: { search: true, list: { priority: 1 }, form: { span: 6 } },
    username: { search: true, list: { width: 160, priority: 1 }, form: { span: 6 } },
    email: { search: true, list: { priority: 3 }, form: { span: 6 } },
    phone: { type: 'phone', list: { priority: 3 }, form: { span: 6 } },
    roleIds: {
      type: 'multiselect',
      ref: { resource: 'admin.role' },
      filter: true,
      list: { priority: 2 },
      form: { span: 12, section: 'access' },
    },
    branchId: {
      type: 'ref',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      filter: true,
      list: { priority: 2 },
      form: { span: 6, section: 'access' },
    },
    active: { filter: true, list: { width: 110, priority: 1 }, form: { span: 6, section: 'access' } },
    password: { type: 'password', list: false, export: false, import: false, form: { span: 6, section: 'access' } },
    lastLoginAt: { readOnly: true, type: 'datetime', list: { priority: 3 } },
  },
  title: 'name',
  columns: ['name', 'username', 'roleIds', 'branchId', 'email', 'phone', 'active', 'lastLoginAt'],
  sections: ['main', 'access'],
  defaultSort: 'name',
  // `viewAs`: looking at the apps as this person, read only (apps/api/src/auth/view-as.ts); no role gives it, so only a
  // super admin
  actions: ['read', 'create', 'update', 'delete', 'export', 'viewAs'],
  card: { title: 'name', subtitle: 'username', meta: ['roleIds'], status: 'active' },
})

/**
 * A role: read only, even for a super admin. Roles and their permissions are the code's (`defaultRoles` in roles.ts, which
 * the API's start writes to the database); the apps give them to people (`admin.user.roleIds`, invites, positions)
 */
export const role = defineResource({
  app: 'admin',
  name: 'role',
  path: 'roles',
  schema: z.object({
    key: z.string().regex(/^[a-z0-9_]{2,40}$/),
    name: z.string().trim().min(1).max(100),
    description: z.string().max(300).nullish(),
    permissions: z.array(z.string()).default([]),
    /**
     * Roles whose holders may give this role (in an invite, or approving a request) — within
     * their own branch when their approval permission is `@branch`. A super admin gives any.
     */
    grantableBy: z.array(z.string()).default([]),
  }),
  fields: {
    name: { search: true, list: { priority: 1 } },
    key: { search: true, list: { width: 160, priority: 3, hidden: true } },
    description: { type: 'textarea', search: true, list: { priority: 2 } },
    userCount: { readOnly: true, type: 'integer', list: { width: 110, align: 'end', priority: 1 } },
    grantableBy: { type: 'multiselect', ref: { resource: 'admin.role' }, list: { priority: 3, hidden: true } },
    permissions: { list: false, export: false },
  },
  title: 'name',
  columns: ['name', 'description', 'userCount', 'grantableBy', 'key'],
  defaultSort: 'name',
  actions: ['read'],
  card: { title: 'name', subtitle: 'description' },
})

export const auditLog = defineResource({
  app: 'admin',
  name: 'auditLog',
  path: 'audit',
  schema: z.object({}),
  fields: {
    at: { type: 'datetime', filter: true, list: { width: 170, priority: 1 } },
    userName: { search: true, list: { priority: 1 } },
    // The assistant that did it for them over MCP (Claude, ChatGPT); empty when they did it in the apps
    via: { filter: true, list: { width: 120, priority: 3 } },
    action: {
      type: 'select',
      options: ['create', 'update', 'delete', 'submit', 'cancel', 'import', 'login', 'print', 'viewAs'],
      filter: true,
      list: { width: 120, priority: 1 },
    },
    subject: { search: true, filter: true, list: { priority: 2 } },
    recordId: { list: { priority: 3 } },
    summary: { search: true, list: { priority: 2 } },
  },
  title: 'summary',
  defaultSort: '-at',
  actions: ['read', 'export'],
})
